import { PlacementId, fromPlacementId } from '../combination-rules/placementIds'
import { EmbroideryTypeKey } from '../combination-rules/types'
import { EmbroideryItem } from '../providers/types'
import { EmbroideryCatalogKey } from '../useEmbroideryVariants'

import type { NewLogoInOrder } from '../useNewLogoInOrder'

/**
 * What a customer is charged for, one key per thing that has its own price. `text` is text at a
 * placement with no product of its own.
 */
export type EmbroideryPriceKey =
  | 'icon'
  | 'text'
  | 'text:chest'
  | 'text:sleeve'
  | 'logo:bw'
  | 'logo:full_color'
  | 'logo_setup'

/**
 * The catalog product key behind each price. There is no combination pricing: a selection costs
 * the sum of its pieces, so two lines of text on two placements buy two of these. Setup isn't
 * listed: its product carries no type tag, so it's found on its own (`findDigitizationVariant`).
 */
export const EMBROIDERY_PRICE_CATALOG_KEYS: Record<
  Exclude<EmbroideryPriceKey, 'logo_setup'>,
  EmbroideryCatalogKey
> = {
  'icon': 'ICON',
  'text': 'TEXT',
  'text:chest': 'TEXT_CHEST',
  'text:sleeve': 'TEXT_SLEEVE',
  'logo:bw': 'LOGO_BW',
  'logo:full_color': 'LOGO_COLOR',
}

/** The price key for text at a placement. */
export function textPriceKey(placementId: PlacementId): EmbroideryPriceKey {
  const { location } = fromPlacementId(placementId)
  switch (location) {
    case 'CHEST':
      return 'text:chest'
    case 'SLEEVE':
      return 'text:sleeve'
    default:
      return 'text'
  }
}

/**
 * What a piece of embroidery would cost at a placement, before the customer has filled it in. This
 * is what the placement cards label themselves with.
 *
 * Logo is absent because its price depends on a colour the customer has not chosen yet — the logo
 * form prices its own options.
 */
export function priceKeyForPlacement(
  typeKey: EmbroideryTypeKey,
  placementId: PlacementId
): EmbroideryPriceKey | undefined {
  if (typeKey === 'text') return textPriceKey(placementId)
  if (typeKey === 'icon') return 'icon'
  return undefined
}

/** What a selected item costs, ignoring any logo setup charge. */
export function priceKeyForItem(item: EmbroideryItem): EmbroideryPriceKey | undefined {
  switch (item.typeKey) {
    case 'text':
      return textPriceKey(item.placementId)
    case 'icon':
      return 'icon'
    case 'logo': {
      const setting = item.values.logoFile?.digitizationSetting
      if (setting === 'BLACK_WHITE') return 'logo:bw'
      if (setting === 'COLOR') return 'logo:full_color'
      return undefined
    }
  }
}

/**
 * How many digitization charges a selection carries. Digitizing is charged once however many
 * placements a logo lands on, and only for a logo new to the system — one already digitized
 * carries `requiresSetup: false` and adds nothing.
 */
export function setupChargeCount(items: readonly EmbroideryItem[]): number {
  const logoIdsNeedingSetup = new Set<string>()
  let untrackedSetups = 0

  for (const item of items) {
    if (item.typeKey !== 'logo' || !item.values.requiresSetup) continue

    const logoId = item.values.logoFile?.id
    // A logo mid-upload has no id yet, so it cannot be deduped against anything and is counted
    // on its own.
    if (logoId === undefined) untrackedSetups += 1
    else logoIdsNeedingSetup.add(logoId)
  }

  return logoIdsNeedingSetup.size + untrackedSetups
}

/**
 * The items as they should be priced on this garment. A new logo another garment already pays the
 * digitizing for is priced without it here, so the order is charged once per new logo.
 */
export function withoutSetupChargedElsewhere(
  items: readonly EmbroideryItem[],
  newLogoInOrder: NewLogoInOrder | undefined
): readonly EmbroideryItem[] {
  if (!newLogoInOrder?.isSetupChargedElsewhere) return items

  return items.map(item =>
    item.typeKey === 'logo' && item.values.logoFile?.id === newLogoInOrder.logoFile.id
      ? { ...item, values: { ...item.values, requiresSetup: false } }
      : item
  )
}
